/**
 * Deal Summary selector-delta telemetry manifest (Rally US2040919).
 *
 * Single declarative table of every Deal Summary value the selector-delta
 * telemetry middleware (US2040385) evaluates on each dispatch. One row per
 * on-wire `selectorKey`. Consumed by `./registerAll.ts`, which hands the whole
 * table to `registerSelectorTelemetry` at bootstrap.
 *
 * CONTRACT SURFACE:
 *   - Each `key` is the exact string that appears in New Relic (`selectorKey`
 *     on `SelectorDelta` events, column names on `SelectorSnapshot` rows) and
 *     in downstream dashboards + the Python replay CLI. Renaming or dropping a
 *     key is a BREAKING change to the telemetry contract — dashboards silently
 *     stop matching. The regression test in `./__tests__/dealSummaryTelemetry`
 *     pins the full key list + order.
 *   - `shape` defaults to `'scalar'` (see `registerSelectorTelemetry`); only
 *     collection-shaped selectors declare it, so the exceptions stand out.
 *   - Registration ORDER is the wire/emission order — keep it stable.
 */
import type { SelectorRegistration } from '@makemydeal/dr-shared-store/telemetry/selectorRegistry';
import type { StateTree } from '@makemydeal/dr-dash-types';

import {
    getRetailPriceOverrideWithFallback,
    getSellingPrice,
    getMonthlyPayment,
    getSelectedTermMonths,
    getDownPayment
} from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import {
    getNetSellingPrice,
    getCapCostReduction,
    getTotalAddons,
    getCapitalizedTax,
    getTotalAmountDue
} from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import { getSummaryDiscount, getSummaryDealerDiscounts } from '@makemydeal/dr-dash-store/selectors/dealerDiscountsSelectors';
import {
    getCapCostTotal,
    getAdjustedCapCost,
    getAddsToCapCostTotal,
    getAdjustedResidualValue,
    getAdjustedResidualPercent,
    getTotalTax,
    getAppliedIncentivesTotalForDisplay,
    getDueAtSigningFromDueInAdvance,
    getCashDueFromCustomer,
    getCashOffsetAmountAddedToPrice,
    getNetCashPayout
} from '@makemydeal/dr-dash-store/selectors/offerRedux';

/**
 * Every Deal Summary selector registered for telemetry, in canonical
 * (wire/emission) order. `shape` omitted ⇒ `'scalar'`.
 */
export const dealSummarySelectorTelemetry: SelectorRegistration<StateTree>[] = [
    { key: 'dealSummary.msrp', selector: getRetailPriceOverrideWithFallback },
    { key: 'dealSummary.sellingPrice', selector: getSellingPrice },
    { key: 'dealSummary.netSellingPrice', selector: getNetSellingPrice },
    { key: 'dealSummary.discount', selector: getSummaryDiscount },
    { key: 'dealSummary.dealerDiscounts', selector: getSummaryDealerDiscounts, shape: 'collection' },
    { key: 'dealSummary.monthlyPayment', selector: getMonthlyPayment },
    { key: 'dealSummary.termMonths', selector: getSelectedTermMonths },
    { key: 'dealSummary.downPayment', selector: getDownPayment },
    { key: 'dealSummary.grossCapCost', selector: getCapCostTotal },
    { key: 'dealSummary.adjCapCost', selector: getAdjustedCapCost },
    { key: 'dealSummary.addsToCapCost.total', selector: getAddsToCapCostTotal },
    { key: 'dealSummary.capCostReduction.total', selector: getCapCostReduction },
    { key: 'dealSummary.adjustedResidual.value', selector: getAdjustedResidualValue },
    { key: 'dealSummary.adjustedResidual.percent', selector: getAdjustedResidualPercent },
    { key: 'dealSummary.addons.total', selector: getTotalAddons },
    { key: 'dealSummary.totalTax', selector: getTotalTax },
    { key: 'dealSummary.leaseTotalTax.capitalized', selector: getCapitalizedTax },
    { key: 'dealSummary.rebates.total', selector: getAppliedIncentivesTotalForDisplay },
    { key: 'dealSummary.dueAtSigning.total', selector: getDueAtSigningFromDueInAdvance },
    { key: 'dealSummary.dueAtSigningPaidBy.total', selector: getTotalAmountDue },
    { key: 'dealSummary.cashDueFromCustomer', selector: getCashDueFromCustomer },
    { key: 'dealSummary.insufficientFunds', selector: getCashOffsetAmountAddedToPrice },
    { key: 'dealSummary.netCashPayout', selector: getNetCashPayout }
];
